// Update app in Orders, for a manager (UC-57, #650).
//
// The till's order store checks the till files site for a newer signed Bosla
// app after each menu check, and answers what it found on GET
// /store/app-update (internal/orderstore, native level 8). Orders reads it
// when it opens and again every APP_UPDATE_POLL_MS, and shows a manager one
// standing Notice for it. Update app is POST /store/app-update: the store
// downloads the file once, checks it and keeps it. A lock then installs it
// at rest (askAtLock in rest.ts); the till's own lock through installWhenQuiet
// here. Once it is kept, the Notice offers a manager Install now (#925),
// which installs as soon as nothing is on its way (InstallNow).
//
// The screen keeps nothing of it: the state lives in the store, and Orders
// reads it afresh each time it opens. A state or a failure the screen does not
// know shows nothing, as the store's contract asks, and so does any state for
// a cashier who is not a manager, whom the store refuses the press.

import { store } from '../api/client';
import type { MessageKey } from '../i18n/messages';
import type { components } from '../api/store';
import { isHeld } from './printing';
import { installAtRest } from './shell';

/** What the store answers about an app update. */
export type Update = components['schemas']['StoreAppUpdateBody'];

/** How often Orders reads the update again while it is open. */
export const APP_UPDATE_POLL_MS = 2000;

/** How long a press waits before its button says what it is doing. */
export const PRESS_SLOW_MS = 300;

/** What the Notice shows for one state. */
export interface UpdateNotice {
	/** The line, in the catalogue. */
	text: MessageKey;
	/** The newer app's version, as 1.652.0, which the offered line names. */
	version: string;
	tone: 'neutral' | 'flag';
	/**
	 * Update app: `press` to start the download, `downloading` while it runs,
	 * off and saying so; once it is kept, `install`, Install now (#925).
	 */
	action: 'press' | 'downloading' | 'install' | null;
	/** How much of the file came, 0 to 100, while it downloads. */
	meter: number | null;
}

const failures: Record<NonNullable<Update['failure']>, MessageKey> = {
	download_stopped: 'appUpdateStopped',
	file_not_checked: 'appUpdateNotChecked',
};

// The line for a failure the screen knows; a name it does not know, an
// Object.prototype member's included, has none.
function failureText(failure: string | undefined): MessageKey | null {
	if (failure === undefined || !Object.hasOwn(failures, failure)) return null;
	return failures[failure as keyof typeof failures];
}

/** The Notice for the store's last answer, or none. */
export function updateNotice(update: Update | null, isManager: boolean): UpdateNotice | null {
	if (!isManager || update === null) return null;
	const version = update.version ?? '';
	switch (update.state) {
		case 'offered':
			if (version === '') return null;
			return { text: 'appUpdateOffered', version, tone: 'neutral', action: 'press', meter: null };
		case 'downloading':
			return {
				text: 'appUpdateDownloading',
				version,
				tone: 'neutral',
				action: 'downloading',
				meter: share(update.receivedBytes ?? 0, update.sizeBytes ?? 0),
			};
		case 'ready':
			return { text: 'appUpdateReady', version, tone: 'neutral', action: 'install', meter: null };
		case 'failed': {
			const text = failureText(update.failure);
			if (text === null) return null;
			return { text, version, tone: 'flag', action: 'press', meter: null };
		}
		default:
			return null;
	}
}

// The share of the file that came, from 0 to 100.
function share(received: number, size: number): number {
	if (size <= 0) return 0;
	return Math.max(0, Math.min(100, Math.floor((received * 100) / size)));
}

/**
 * Whether the store's print queue holds no job on its way: none queued and
 * none printing. A job held for a decision (#515) waits for a person and does
 * not count. A queue the store does not answer counts as busy.
 */
export async function printQueueQuiet(): Promise<boolean> {
	try {
		const { data, error } = await store.GET('/store/print-jobs');
		if (error !== undefined || !data) return false;
		return !data.jobs.some(
			(job) => (job.state === 'queued' || job.state === 'printing') && !isHeld(job),
		);
	} catch {
		return false;
	}
}

/**
 * The till's own lock's install ask: the shell's install_at_rest, only while
 * the print queue is quiet. The store lists the queue to a signed-in session,
 * locked or not, so the signed-out lock screen waits past every print's
 * deadline instead (settledLockAsk in rest.ts). An install stops the order
 * store and, on Windows, the print host, so a receipt or a kitchen slip on
 * its way would end uncertain
 * (decision 2026-10-27-app-update-installs-at-any-safe-moment: no write on
 * its way). `atRest` is asked again once the queue answers, since a cashier
 * may come to the lock meanwhile. True when the install started.
 */
export async function installWhenQuiet(
	install: () => Promise<boolean> = installAtRest,
	atRest: () => boolean = () => true,
): Promise<boolean> {
	if (!(await printQueueQuiet()) || !atRest()) return false;
	return install();
}

/** How often a pressed Install now asks again while a print is on its way. */
export const INSTALL_NOW_ASK_MS = 2000;

/**
 * Install now, a manager's press on the ready Notice (#925). With nothing on
 * its way the till installs at once; otherwise its button says the till
 * installs when it is free, and it installs as soon as it is. Two things
 * must be free: the page, unlocked, with no write on its way and no form
 * holding typing (installNowAsks in rest.ts), which the till watches and
 * gives each ask as `free`, and the store's print queue (printQueueQuiet),
 * asked at each ask, which the till repeats every INSTALL_NOW_ASK_MS while it
 * waits. A second press while it waits starts nothing more. The wait ends
 * once the shell answers: an install started closes the app, or Android's
 * installer holds it, and one the shell refused (no checked update, a screen
 * bundle on trial) leaves the Notice offering Install now again. A lock ends
 * it too (cancel). The wait lives in the till, not in Orders, so it holds
 * when the manager leaves Orders.
 */
export class InstallNow {
	/** Pressed, and the shell has not answered yet. */
	waiting = $state(false);

	#asking = false;
	#install: () => Promise<boolean>;

	constructor(install: () => Promise<boolean> = installAtRest) {
		this.#install = install;
	}

	/** The manager's press; one while it waits starts nothing more. */
	press(): void {
		this.waiting = true;
	}

	/**
	 * End the wait with no install: the till locked, and from then on the
	 * lock's own ask installs (tillAsk in rest.ts). So a wait never outlives
	 * the manager's session into another cashier's.
	 */
	cancel(): void {
		this.waiting = false;
	}

	/**
	 * One ask while it waits: nothing while a write is on its way (`free`
	 * false) or a print is, and otherwise the shell's install. `free` is asked
	 * again after the queue answers, since a sale can start meanwhile. Asks
	 * never overlap.
	 */
	async ask(free: () => boolean): Promise<void> {
		if (!this.waiting || this.#asking || !free()) return;
		this.#asking = true;
		try {
			if (!(await printQueueQuiet()) || !free()) return;
			try {
				await this.#install();
			} catch {
				// No shell answer: the shell started nothing.
			}
			this.waiting = false;
		} finally {
			this.#asking = false;
		}
	}
}

/** The store's app update as Orders reads it, and the manager's press. */
export class AppUpdate {
	/** The store's last answer; null until one came. */
	update = $state<Update | null>(null);
	/** A press is on its way. */
	pressing = $state(false);
	/** A press has been on its way past PRESS_SLOW_MS, so its button says so. */
	slow = $state(false);

	// Reads and presses can overlap. An answer older than one already taken
	// is dropped, and a call that fails never drops a good answer. No read
	// starts while a press is on its way, so the press's answer is the newest.
	#started = 0;
	#taken = 0;

	#take(mine: number, update: Update | undefined): void {
		if (update === undefined || mine < this.#taken) return;
		this.#taken = mine;
		this.update = update;
	}

	/** Read the state from the store again; nothing while a press is on its way. */
	async read(): Promise<void> {
		if (this.pressing) return;
		const mine = ++this.#started;
		try {
			const { data, error } = await store.GET('/store/app-update');
			if (error === undefined) this.#take(mine, data);
		} catch {
			// The store did not answer: the last state stands until the next read.
		}
	}

	/**
	 * Update app: start the download. A second press while one is on its way
	 * sends nothing. A press the store refused or did not answer reads the
	 * state again, so the Notice says what the store holds.
	 */
	async press(): Promise<void> {
		if (this.pressing) return;
		this.pressing = true;
		this.slow = false;
		const timer = setTimeout(() => (this.slow = true), PRESS_SLOW_MS);
		const mine = ++this.#started;
		let taken = false;
		try {
			const { data, error } = await store.POST('/store/app-update');
			if (error === undefined && data !== undefined) {
				this.#take(mine, data);
				taken = true;
			}
		} catch {
			// No answer: the read below says what the store holds.
		} finally {
			clearTimeout(timer);
			this.pressing = false;
			this.slow = false;
		}
		if (!taken) await this.read();
	}

	/** Read now and every `every` ms, until the stop it returns. */
	watch(every: number = APP_UPDATE_POLL_MS): () => void {
		void this.read();
		const timer = setInterval(() => void this.read(), every);
		return () => clearInterval(timer);
	}
}
